<template>
  <client-only>
    <div>
      <div v-if="showLogin" class="checkout checkout-login">
        <div class="row">
          <CheckoutLogIn
            @checkoutAsNew="checkoutAsNew"
            @checkoutWithThirdPartySuccess="checkoutWithThirdPartySuccess"
          />
        </div>
      </div>
      <div v-show="!showLogin" class="b24 b24-checkout-page">
        <!-- Show checkout header when global "showHeader" is false -->
        <div
          v-if="!inMobileApp && !showHeader"
          class="site-header-container simple-header checkout-header"
        >
          <header>
            <div class="utility-navigation">
              <div class="left">
                <nuxt-link to="/store/" class="show">
                  Continue shopping
                </nuxt-link>
              </div>
              <div class="logo">
                <nuxt-link to="/" class="nuxt-link-active">
                  <span class="sr-only">Wine Access</span>
                </nuxt-link>
              </div>
            </div>
          </header>
        </div>
        <!-- For when we detect that a user has entered an existing user's e-mail -->
        <LogInModal v-if="modalOpen" @close="modalOpen = false" />
        <CheckoutModalAddressVerification />
        <CheckoutLoading />
        <div v-if="userLoaded && !orderSuccess" class="site-width">
          <div class="row d-flex b24-checkout">
            <template v-if="!showLogin">
              <div
                :class="{
                  columns: true,
                  'main-column': true,
                  'in-mobile-app': inMobileApp,
                }"
              >
                <h1 class="f25">Checkout</h1>
                <CheckoutRegistration
                  v-if="isSignUpState"
                  form-name="checkout-new-user-signup"
                  @registrationSuccess="registrationSuccess"
                />
                <template v-if="checkoutBasketLoaded && !isSignUpState">
                  <ShippingSection @saveSuccess="shippingSuccess" />
                  <template v-if="!showOnlyShippingSection">
                    <DeliveryDetails
                      ref="deliveryDetails"
                      :checkout-state="checkoutState"
                      :cart="cart"
                      :force-holiday-warning="forceHolidayWarning"
                      :saved="deliveryDetailsSaved"
                      @saveSuccess="deliveryDetailsSuccess"
                    />
                    <PackagingChoices
                      v-if="!pptoAlreadyHadIcePacks"
                      :checkout-state="checkoutState"
                      :cart="cart"
                    />
                    <CheckoutPayment
                      v-if="showPaymentSection"
                      @saveSuccess="paymentSuccess"
                      @promoCodeAdded="promoCodeAdded"
                    />
                    <div :class="{ 'pt-44 bordered-section': !smsOptedIn }">
                      <div v-if="!smsOptedIn" class="row lg-up">
                        <div class="columns">
                          <div
                            class="flex-group gap-32 align-top w-100 sms-group"
                          >
                            <label class="toggle-switch-item">
                              <input
                                v-model="
                                  smsState.receive_shipping_updates_by_sms
                                "
                                type="checkbox"
                                class="toggle-checkbox"
                                @change="updateSmsToggle"
                              />
                              <span class="toggle-switch"></span>
                              <span class="toggle-label">
                                <strong>
                                  Get text updates.
                                </strong>
                                <span class="small"
                                  >Adult signature required for delivery.</span
                                >
                              </span>
                            </label>
                            <div style="width: 50%;">
                              <FormulateInput
                                v-model="smsState.sms_number"
                                name="sms_number"
                                validation="bail|requiredIfOptedIn|validPhoneNumber"
                                :validation-rules="{
                                  requiredIfOptedIn,
                                  validPhoneNumber,
                                }"
                                :validation-messages="{
                                  requiredIfOptedIn:
                                    'Please enter a number we can text updates to',
                                  validPhoneNumber:
                                    'Mobile number is incorrect, please use this format: (XXX)-XXX-XXXX',
                                }"
                                error-behavior="value"
                                label="Mobile Number"
                              />
                            </div>
                          </div>
                        </div>
                        <span
                          v-if="smsState.receive_shipping_updates_by_sms"
                          class="columns fineprint place-order-fineprint"
                        >
                          I authorize Wine Access to provide shipping updates on
                          my orders to the number I provided, via text (SMS).
                          Message and data rates may apply. To opt-out at any
                          time, please reply STOP.
                        </span>
                      </div>
                      <div
                        class="row place-order lg-up"
                        :class="{ 'place-order-tight': smsOptedIn }"
                      >
                        <div
                          v-if="checkoutState.placeOrder.errors.length > 0"
                          class="columns"
                        >
                          <ErrorList
                            :suppress-label="true"
                            :errors="checkoutState.placeOrder.errors"
                          />
                        </div>
                        <div class="columns">
                          <PlaceOrderButton
                            @placeOrderClicked="placeOrderClicked"
                          />
                        </div>
                      </div>
                      <div class="mobile-place-order-footer">
                        <div
                          v-if="checkoutState.placeOrder.errors.length > 0"
                          class="d-flex"
                        >
                          <ErrorList
                            :suppress-label="true"
                            :errors="checkoutState.placeOrder.errors"
                          />
                          <br />
                        </div>
                        <div class="d-flex">
                          <div>
                            Order Total:
                            <strong
                              >${{ formatPrice(cart.grand_total) }}</strong
                            >
                          </div>
                          <PlaceOrderButton
                            @placeOrderClicked="placeOrderClicked"
                          />
                        </div>
                      </div>
                      <div class="row lg-up">
                        <div class="columns">
                          <CheckoutTermsAndConditionsAndWarnings
                            :as-bold="true"
                          />
                        </div>
                      </div>
                    </div>
                    <div class="row lg-up">
                      <div class="columns">
                        <CustomerServiceFooter />
                      </div>
                    </div>
                  </template>
                </template>
              </div>
              <div
                v-if="checkoutBasketLoaded || isSignUpState"
                class="columns sidebar bg-shade"
              >
                <div class="plr-1r">
                  <div class="row">
                    <div class="columns">
                      <Accordion v-slot="scope" :multiple="true">
                        <AccordionItem
                          :expanded-by-default="true"
                          :event-type="'Checkout'"
                          title="Review Order"
                          @expanded="scope.expanded"
                        >
                          <OrderSummary
                            :cart="cart"
                            @loadingStart="
                              $store.dispatch(
                                'increaseWorkgroupBusyCounter',
                                'checkout'
                              )
                            "
                            @loadingEnd="
                              $store.dispatch(
                                'decreaseWorkgroupBusyCounter',
                                'checkout'
                              )
                            "
                          />
                        </AccordionItem>
                        <AccordionItem
                          ref="orderTotalAccordion"
                          :title="`Order Total: $${formatPrice(
                            cart.grand_total
                          )}`"
                          :expanded-by-default="
                            checkoutState.payment.appliedGiftCard
                          "
                          :event-type="'Checkout'"
                          @expanded="scope.expanded"
                        >
                          <div class="order-total">
                            <CartLedger
                              :cart="cart"
                              :is-checkout="true"
                              :ppto-already-had-ice-packs="
                                pptoAlreadyHadIcePacks
                              "
                              :styling="{
                                border: { classes: [''] },
                                subtotal: {
                                  classes: [isSignUpState ? 'total' : ''],
                                },
                              }"
                            />
                          </div>
                        </AccordionItem>
                      </Accordion>
                    </div>
                  </div>
                  <CheckoutRecommendations />
                  <div class="row place-order lg-up">
                    <div
                      v-if="checkoutState.placeOrder.errors.length > 0"
                      class="columns errorlist"
                    >
                      <ErrorList
                        :suppress-label="true"
                        :errors="checkoutState.placeOrder.errors"
                      />
                      <br />
                    </div>
                    <div class="columns">
                      <PlaceOrderButton
                        @placeOrderClicked="placeOrderClicked"
                      />
                      <div class="fineprint place-order-fineprint">
                        <CheckoutTermsAndConditions />
                        <CheckoutPassageOfTitle />
                      </div>
                    </div>
                  </div>
                  <div v-if="!smsOptedIn" class="row">
                    <div class="columns">
                      <div
                        class="flex-group gap-24 flex-column align-top w-100 sms-group"
                      >
                        <label class="toggle-switch-item">
                          <input
                            v-model="smsState.receive_shipping_updates_by_sms"
                            type="checkbox"
                            class="toggle-checkbox"
                            @change="updateSmsToggle"
                          />
                          <span class="toggle-switch"></span>
                          <span class="toggle-label">
                            <strong>
                              Get text updates.
                            </strong>
                            <span class="small"
                              >Adult signature required for delivery.</span
                            >
                          </span>
                        </label>
                        <div class="grow-1">
                          <FormulateInput
                            v-model="smsState.sms_number"
                            name="sms_number"
                            validation="bail|requiredIfOptedIn|validPhoneNumber"
                            :validation-rules="{
                              requiredIfOptedIn,
                              validPhoneNumber,
                            }"
                            :validation-messages="{
                              requiredIfOptedIn:
                                'Please enter a number we can text updates to',
                              validPhoneNumber:
                                'Mobile number is incorrect, please use this format: (XXX)-XXX-XXXX',
                            }"
                            error-behavior="value"
                            label="Mobile Number"
                          />
                        </div>
                      </div>
                      <div
                        v-if="smsState.receive_shipping_updates_by_sms"
                        class="fineprint place-order-fineprint"
                      >
                        <p>
                          I authorize Wine Access to provide shipping updates on
                          my orders to the number I provided, via text (SMS).
                          Message and data rates may apply. To opt-out at any
                          time, please reply STOP.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="lg-down">
                    <div class="row">
                      <div class="columns">
                        <CheckoutTermsAndConditionsAndWarnings
                          :as-bold="true"
                        />
                      </div>
                    </div>
                    <div class="row">
                      <div class="columns">
                        <CustomerServiceFooter />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
        <template v-if="orderSuccess && order">
          <CheckoutThankYouNew v-if="!orderSuccessIsPPTO" :order="order" />
          <CheckoutThankYou v-else :show-refer-a-friend="false" :order="order">
            <template #greeting>
              <h1 id="order-greeting">
                {{ finalBottlesAdded }} bottle{{
                  finalBottlesAdded !== 1 ? 's have' : ' has'
                }}
                been added to your shipment
              </h1>
            </template>
            <template #subheader>
              <p v-if="order.ppto_order">
                We have updated your order #{{ order.number }} to include
                {{ finalBottlesAdded !== 1 ? 'these wines' : 'this wine' }}.
              </p>
              <p>
                You will receive a confirmation email shortly at
                <span id="user-email">{{ $auth.user.email }}</span>
              </p>
            </template>
          </CheckoutThankYou>
        </template>
      </div>
    </div>
  </client-only>
</template>

<script>
import { sumBy } from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import { storeUtils } from '~/../common/utils/storeUtils'
import ErrorList from '~/../common/components/ErrorList.vue'
import CheckoutLoading from '~/../common/components/Loading/CheckoutLoading.vue'
import { formatPrice } from '~/../common/utils/checkout'
import { validatePhoneNumber } from '~/../common/utils/phone'
import { pushEvent, pushPageView, userLoaded } from '~/../common/utils/user'
import {
  fetchDeliveryMethodsAndDates,
  getBasket,
  getCheckoutBasket,
  handleSectionError,
  hydrateSMSState,
  newSMSState,
  placeOrder,
} from '~/../common/services/checkoutServices'
import CartLedger from '~/components/Cart/CartLedger.vue'
import CheckoutPassageOfTitle from '~/components/Checkout/CheckoutPassageOfTitle.vue'
import CheckoutTermsAndConditions from '~/components/Checkout/CheckoutTermsAndConditions.vue'
import CheckoutTermsAndConditionsAndWarnings from '~/components/Checkout/CheckoutTermsAndConditionsAndWarnings.vue'
import CheckoutModalAddressVerification from '~/components/Checkout/CheckoutModalAddressVerification'
import CustomerServiceFooter from '~/components/Checkout/CustomerServiceFooter.vue'
import PlaceOrderButton from '~/components/Checkout/PlaceOrderButton.vue'
import CheckoutThankYou from '~/components/Checkout/CheckoutThankYou.vue'
import CheckoutThankYouNew from '~/components/Checkout/CheckoutThankYouNew.vue'
import ga4 from '~/../common/utils/ga4'

export default {
  name: 'CheckoutPage',
  mode: 'client',
  ssr: false,
  fetchOnServer: false,
  components: {
    CheckoutThankYou,
    CheckoutThankYouNew,
    CheckoutLoading,
    CheckoutPassageOfTitle,
    PlaceOrderButton,
    CustomerServiceFooter,
    CheckoutTermsAndConditionsAndWarnings,
    CheckoutTermsAndConditions,
    CartLedger,
    CheckoutModalAddressVerification,
    CheckoutRecommendations: () =>
      import('~/components/Checkout/CheckoutRecommendations'),
    CheckoutLogIn: () => import('~/components/Checkout/LogIn'),
    LogInModal: () => import('~/../common/components/LogIn/LogInModal'),
    OrderSummary: () => import('~/components/Checkout/OrderSummary'),
    CheckoutRegistration: () =>
      import('~/components/Checkout/CheckoutRegistration'),
    CheckoutPayment: () => import('~/components/Checkout/CheckoutPayment'),
    DeliveryDetails: () => import('~/components/Checkout/DeliveryDetails'),
    PackagingChoices: () =>
      import('~/components/Checkout/PackagingChoices.vue'),
    ShippingSection: () => import('~/components/Checkout/ShippingSection'),
    Accordion: () => import('~/../common/components/Accordion/Accordion'),
    AccordionItem: () =>
      import('~/../common/components/Accordion/AccordionItem'),
    ErrorList,
  },
  beforeRouteLeave(to, from, next) {
    this.$store.dispatch('setShowHeader', true)
    this.$store.dispatch('setShowFooter', true)

    this.$axios.interceptors.request.eject(this.requestInterceptor)
    this.$axios.interceptors.response.eject(this.responseInterceptor)

    next()
  },
  data() {
    const showLogin = !this.$auth.state.loggedIn
    return {
      checkoutBasket: {},
      initialized: false,
      checkoutBasketLoaded: false,
      smsState: newSMSState(),
      smsOptedIn: true,
      submitErrors: {},
      showLogin,
      orderSuccess: false,
      orderSuccessIsPPTO: false,
      responseInterceptor: null,
      requestInterceptor: null,
      order: null,
      deliveryDetailsSaved: false,
      // if the checkout page is talking to the following API endpoints, do not consider the page to be 'loading'
      nonBlockingEndpoints: [
        '/api/catalog/topoff/',
        '/api/logic/esp-push/',
        '/api/logic/esp-page/',
        '/api/customer/promo_banner/',
        '/api/customer/notification/product/',
      ],
      // Ignore these API endpoints for page loading only on initial request, subsequent requests WILL put page in loading state
      nonBlockingEndpointsInitial: [
        { url: '/api/address/pick-up-locations/ups/', initial: true },
        { url: '/api/address/pick-up-locations/fedex/', initial: true },
      ],
      forceHolidayWarning: false,
      pageStart: undefined,
    }
  },
  head() {
    return {
      script: [
        {
          src: `https://maps.googleapis.com/maps/api/js?key=${this.settingsVars.GOOGLE_MAPS_API_KEY}&libraries=places`,
          async: true,
          defer: true,
        },
      ],
    }
  },
  computed: {
    ...mapGetters([
      'cart',
      'settingsVars',
      'modalOpen',
      'pptoEligibleOrder',
      'inMobileApp',
      'showHeader',
      'checkoutState',
    ]),
    showOnlyShippingSection() {
      return (
        !this.checkoutState.shipping.selectedAddress &&
        !this.checkoutState.shipping.selectedPickupAddress
      )
    },
    finalBottlesAdded() {
      return sumBy(this.order.ppto_order.lines, (line) => line.quantity)
    },
    loggedIn() {
      return this.$auth.loggedIn
    },
    userLoaded() {
      return userLoaded(this.$auth)
    },
    isSignUpState: {
      set(value) {
        this.checkoutState.isSignUp = value
        this.$store.dispatch('setCheckoutState', this.checkoutState)
      },
      get() {
        return this.checkoutState.isSignUp
      },
    },
    sections() {
      const sections = [
        this.checkoutState.shipping,
        this.checkoutState.deliveryDetails,
        this.checkoutState.payment,
      ]
      if (this.isSignUpState) {
        sections.unshift(this.checkoutState.registration)
      }
      return sections
    },
    errorSections() {
      return this.sections.filter((x) => x.errors.length > 0)
    },
    showPaymentSection() {
      return (
        this.cart.grand_total > 0 ||
        this.cart.is_as_you_go ||
        this.checkoutState.payment.appliedGiftCard
      )
    },
    selectedPPTOOrder() {
      return this.checkoutState.pptoEligibleOrders.find(
        (obj) => obj.id === this.checkoutState.postPurchaseOrder
      )
    },
    pptoAlreadyHadIcePacks() {
      return this.selectedPPTOOrder && this.selectedPPTOOrder.ice_pack_allowed
    },
  },
  watch: {
    userLoaded(newValue) {
      if (newValue) {
        this.isSignUpState = !this.$auth.loggedIn
      }
    },
    cart: {
      async handler(newCart) {
        await storeUtils.doBusyWork(this.$store, 'checkout', async () => {
          if (
            newCart.num_bottles === 0 &&
            !this.orderSuccess &&
            this.$auth.loggedIn
          ) {
            this.$router.push('/store/')
          } else if (newCart.eligible_as_ppto && this.checkoutBasketLoaded) {
            await this.$store.dispatch('fetchPPTOEligibleOrder')
          }
          const datesAndMethods = await fetchDeliveryMethodsAndDates(
            this.checkoutState,
            this.$axios
          )
          this.setCheckoutDeliveryMethods(datesAndMethods.deliveryMethods)
        })
      },
    },
    loggedIn: {
      async handler(newValue, oldValue) {
        if (this.initialized) return
        if (newValue === oldValue) return
        const loginShowed = this.showLogin
        this.showLogin = !newValue
        if (newValue) {
          if (loginShowed) {
            // we're logged in from the login window
            this.isSignUpState = false
            await this.fetchCheckoutData()
            this.initialized = true
          } else if (this.isSignUpState) {
            // we're logged in from the sign up window
            await this.fetchCheckoutData()
            this.isSignUpState = false
            this.registrationSuccess()
            this.initialized = true
          }
        }
      },
    },
  },
  mounted() {
    // This tracking event is for Segment
    pushEvent(
      this.$axios,
      {
        metric_name: 'checkout started',
        url_path: this.$route.fullPath,
      },
      this.$store
    )
  },
  async beforeMount() {
    this.pageStart = Date.now() // start tracking page load time at beforeMount
    this.$store.dispatch('setShowHeader', false)
    this.$store.dispatch('setShowFooter', false)

    if (this.$route.query.force_holiday_warning === 'true') {
      this.forceHolidayWarning = true
    }

    if (this.loggedIn) {
      this.showLogin = false
      this.isSignUpState = false
    }

    if (this.initialized) return

    if (this.loggedIn) {
      await this.fetchCheckoutData()
      this.initialized = true
      this.signUpState = false
      this.showLogin = false
      this.registrationSuccess()
    }
    const PO = this.checkoutState.placeOrder
    if (PO && PO.errors && PO.errors.length > 0) {
      this.checkoutState.placeOrder.errors = []
      this.$store.dispatch('setCheckoutState', this.checkoutState)
    }
  },
  methods: {
    formatPrice,
    ...mapActions(['setCheckoutDeliveryMethods']),
    checkoutAsNew() {
      window.scrollTo(0, 0)
      this.showLogin = false
      this.isSignUpState = true
    },

    checkoutWithThirdPartySuccess() {
      window.scrollTo(0, 0)
      this.showLogin = false
      this.isSignUpState = true
    },
    async fetchCheckoutData() {
      this.$store.dispatch('setCartLoading', true)
      try {
        await storeUtils.doBusyWork(this.$store, 'checkout', async () => {
          try {
            const resetIcePack = false
            const updateDefaults = true
            this.checkoutBasket = await getCheckoutBasket(
              this.$axios,
              this.$sentry,
              resetIcePack,
              updateDefaults
            )
            hydrateSMSState(this.checkoutBasket, this.smsState)
            this.smsOptedIn = !!(
              this.smsState.receive_shipping_updates_by_sms &&
              this.smsState.sms_number
            )
            this.checkoutBasketLoaded = true
            this.$store.dispatch('updateCart', this.checkoutBasket)
            if (!this.initialized) {
              ga4.trackBeginCheckout({
                products: this.cart.lines,
              })
            }
          } finally {
            this.updateLoadingStatus(false)
          }
        })
      } finally {
        this.$store.dispatch('setCartLoading', false)
      }
    },
    registrationSuccess() {
      this.checkoutState.registration.errors = []
    },
    shippingSuccess() {
      this.checkoutState.shipping.errors = []
    },
    deliveryDetailsSuccess() {
      this.checkoutState.deliveryDetails.errors = []
      this.deliveryDetailsSaved = true
      ga4.track('add_delivery_info')
    },
    paymentSuccess() {
      this.checkoutState.payment.errors = []
      if (this.isSignUpState) {
        this.isSignUpState = false
      }
      ga4.track('add_payment_info')
    },
    updateSmsToggle() {
      // forces re-validation after toggled
      const temp = this.smsState.sms_number
      this.smsState.sms_number = ''
      this.$nextTick(() => {
        this.smsState.sms_number = temp
      })
    },
    requiredIfOptedIn({ value }) {
      return this.smsState.receive_shipping_updates_by_sms
        ? !!this.smsState.sms_number
        : true
    },
    validPhoneNumber({ value }) {
      if (this.smsState.receive_shipping_updates_by_sms) {
        return validatePhoneNumber(value, true)
      }
      return true
    },
    clearSectionErrors() {
      this.sections.forEach((section) => {
        section.errors = []
      })
    },
    updateLoadingStatus(newStatus) {
      if (this.$refs.checkoutLoading)
        this.$refs.checkoutLoading.loading = newStatus
    },
    promoCodeAdded() {
      this.$refs.orderTotalAccordion.expand()
    },
    async placeOrderClicked() {
      await storeUtils.doBusyWork(this.$store, 'checkout', async () => {
        this.clearSectionErrors()
        this.checkoutState.placeOrder.errors = []

        if (this.errorSections.length > 0) {
          handleSectionError(this.errorSections[0], this.$nextTick)
        } else {
          try {
            this.order = await placeOrder(
              this.$axios,
              this.$route,
              this.checkoutBasket.id,
              this.checkoutState,
              this.smsState
            )
            // need to save the postPurchaseOrder status b/c checkoutState is clobbered
            this.orderSuccessIsPPTO = !!this.checkoutState.postPurchaseOrder
          } catch (e) {
            if (e.response && e.response.data) {
              const data = e.response.data
              if (Array.isArray(data)) {
                this.checkoutState.placeOrder.errors = data
              } else if (typeof data === 'object' && data !== null) {
                Object.keys(data).forEach((key) => {
                  this.checkoutState.placeOrder.errors.push(...data[key])
                })
              }
            } else {
              this.checkoutState.placeOrder.errors = [
                `${e}` || 'Error attempting to place order',
              ]
            }
            return
          }
          await Promise.all([
            this.$store.dispatch('setShowHeader', true),
            this.$store.dispatch('setShowFooter', true),
          ])

          pushPageView(this.$axios, {
            name: 'checkout-thank-you',
            properties: {
              path: this.$route.fullPath,
              url: window.location.href,
              title: document.title,
              referrer: document.referrer,
            },
          })

          this.orderSuccess = true

          const basket = await getBasket(this.$axios)
          await this.$store.dispatch('updateCart', basket)

          this.$nextTick(() => {
            window.scrollTo(0, 0)
          })
        }
      })
    },
    block(urlRequest, type) {
      let block = true
      if (urlRequest) {
        block = !this.nonBlockingEndpoints.find((x) => urlRequest.includes(x))
        const noBlockInitialObj = this.nonBlockingEndpointsInitial.find((x) =>
          urlRequest.includes(x.url)
        )
        if (noBlockInitialObj && noBlockInitialObj.initial) {
          block = false
          if (type === 'response') {
            noBlockInitialObj.initial = false
          }
        }
      }
      return block
    },
  },
}
</script>
